import { BRAND, type LngLat } from '@chukua/common';
import { Map as MapLibreMap, Marker } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { configureMapLibreWorker } from '../adapters/configure-maplibre-worker';
import { useColorMode } from '../../theme/components/ColorModeProvider';
import { basemapStyleUrl } from '../policies/basemap';
import { CORRIDOR_CENTER, CORRIDOR_ZOOM } from '../policies/map-framing';

/** Close enough to see which building the pin is on. */
const PINNED_ZOOM = 16;

interface UseLocationPickerArgs {
  /** Null while the deployment has no MapTiler key: no map, the place search still sets the location. */
  tileKey: string | null;
  value: LngLat | null;
  onChange: (location: LngLat) => void;
}

/**
 * A small map for one point: click the map, or drag the pin, to set it. Follows the colour mode like the Zones
 * map, and opens on the point (or on the corridor before there is one).
 */
export function useLocationPicker({ tileKey, value, onChange }: UseLocationPickerArgs) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<MapLibreMap | null>(null);
  const markerRef = useRef<Marker | null>(null);
  const latestRef = useRef({ value, onChange });
  const { mode } = useColorMode();

  useEffect(() => {
    latestRef.current = { value, onChange };
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !tileKey) return;
    configureMapLibreWorker();
    const start = latestRef.current.value;
    const created = new MapLibreMap({
      container,
      style: basemapStyleUrl(mode, tileKey),
      center: start ? [start.lng, start.lat] : CORRIDOR_CENTER,
      zoom: start ? PINNED_ZOOM : CORRIDOR_ZOOM,
    });
    const marker = new Marker({ draggable: true, color: BRAND.palettes[mode].accent });
    marker.on('dragend', () => {
      const { lng, lat } = marker.getLngLat();
      latestRef.current.onChange({ lng, lat });
    });
    created.on('click', (event) => latestRef.current.onChange({ lng: event.lngLat.lng, lat: event.lngLat.lat }));
    markerRef.current = marker;
    setMap(created);
    return () => {
      markerRef.current = null;
      setMap(null);
      marker.remove();
      created.remove();
    };
  }, [tileKey, mode]);

  useEffect(() => {
    const marker = markerRef.current;
    if (!map || !marker) return;
    if (!value) {
      marker.remove();
      return;
    }
    marker.setLngLat([value.lng, value.lat]).addTo(map);
  }, [map, value]);

  /** Brings a place picked by name into view. */
  function showLocation(location: LngLat) {
    map?.flyTo({ center: [location.lng, location.lat], zoom: PINNED_ZOOM });
  }

  return { containerRef, showLocation };
}
